/**
 * 屋里桌上的小相框：TA 带回来的照片（家园改版：看带回的照片或经历要形成流程，c84a FRONTEND-MUST-DELIVER 第 3 条）。
 * 真实接口：GET /pets/{pet_id}/album（最新几张，已成图、没移除、不是幻想主题的；后端按拍摄时刻倒序）。纯读，不触发任何生成。
 * - 有照片：窗下桌上、杯子旁边立一个小相框，框里是最新一张；点它在原地打开一张面板，列最近几张：图、TA 配的话、拍在哪、哪天。
 * - 没有照片、相册没开放（409）、读失败、还在读：桌上什么都不放（不拿别的图冒充，也不挂一个空框）。
 * - 桌上那张图读不出来（UI2-ALBUM-ERR-01）：换下一张能显示的（都是 TA 自己带回来的照片）；全都读不出来就不摆，不露裂图。
 *   相框和面板里的图各管各的：面板里一张读不出来只影响那一格（UI5198），相框照旧。
 * 只由 HomePage 传进场景（HomeBody 的 tablePhoto），保证在服务与查询客户端里面；场景只在屋里画它。
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { PetPhoto } from "@/shared/contracts";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, Sheet } from "@/shared/ui";
import { env } from "@/shared/config/env";
import { FRAME, plateScale, toStage } from "./homeStage";

/**
 * 相框底边中点立在舞台上的哪一点、多大（舞台像素）：屋内底图（r7k room 原图）茶几桌面左后方、那摞书后面，
 * 原图 (455, 642)、62×76 换算过来。茶几遮挡片抬到 TA 上面时，相框跟着抬到茶几上面（home.css --ps-tabletop-z）。
 */
const PHOTO_FOOT = toStage("inside", { x: 455, y: 642 });
export const PHOTO_FRAME = {
  x: PHOTO_FOOT.x,
  y: PHOTO_FOOT.y,
  width: Math.round(62 * plateScale("inside")),
  height: Math.round(76 * plateScale("inside")),
} as const;
/** 面板里列几张 */
export const ALBUM_LIMIT = 6;

export const albumKey = (userId: string, petId: string) => ["pets", "album", userId, petId, ALBUM_LIMIT] as const;

/** 一张图的身份：照片 id＋地址（地址换了就是新的一张，可以重新挂） */
const mediaKey = (photo: PetPhoto) => JSON.stringify([photo.photo_id, photo.asset.media_url]);

/** 能摆出来的：已成图的图片（有地址） */
export function hangable(photos: readonly PetPhoto[]): PetPhoto[] {
  return photos.filter((p) => p.asset.kind === "image" && Boolean(p.asset.media_url));
}

/** 拍在哪：地点名 → 城市；都没有就不说 */
export function placeText(photo: PetPhoto): string | null {
  return photo.place.label ?? photo.place.city ?? null;
}

type AlbumOwner = {
  userId: string | null; petId: string; householdPetId: string | null; mode: string;
  pets: ReturnType<typeof useServices>["pets"]; client: QueryClient; serviceId: number; epoch: number;
};
const albumServiceIds = new WeakMap<object, number>();
let nextAlbumService = 0;
let nextAlbumEpoch = 0;
class AlbumScopeEnded extends Error {
  constructor() { super("Album context ended"); }
}
function newAlbumOwner(userId: string | null, petId: string, householdPetId: string | null, mode: string, pets: AlbumOwner["pets"], client: QueryClient): AlbumOwner {
  let serviceId = albumServiceIds.get(pets);
  if (serviceId === undefined) { serviceId = ++nextAlbumService; albumServiceIds.set(pets, serviceId); }
  return { userId, petId, householdPetId, mode, pets, client, serviceId, epoch: ++nextAlbumEpoch };
}

export function HomeAlbumFrame({ petId, petName }: { petId: string; petName: string }) {
  const { pets } = useServices();
  const client = useQueryClient();
  const household = useOptionalCurrentHousehold();
  const userId = household?.userId?.trim() || null;
  const householdPetId = household?.pet?.pet_id ?? null;
  const [owner, setOwner] = useState(() => newAlbumOwner(userId, petId, householdPetId, env.dataMode, pets, client));
  const committed = useRef<AlbumOwner | null>(null);
  useLayoutEffect(() => {
    committed.current = owner;
    return () => { if (committed.current === owner) committed.current = null; };
  }, [owner]);
  if (owner.userId !== userId || owner.petId !== petId || owner.householdPetId !== householdPetId || owner.mode !== env.dataMode || owner.pets !== pets || owner.client !== client) {
    setOwner(newAlbumOwner(userId, petId, householdPetId, env.dataMode, pets, client));
    return null;
  }
  if (owner.mode !== "fixture" && (!userId || !petId.trim() || householdPetId !== petId)) return null;
  return <OwnedHomeAlbum key={owner.epoch} owner={owner} petName={petName} isCurrent={() => committed.current === owner} />;
}

function OwnedHomeAlbum({ owner, petName, isCurrent }: { owner: AlbumOwner; petName: string; isCurrent: () => boolean }) {
  const { petId, pets } = owner;
  const mount = useRef({ active: false, generation: 0 });
  useLayoutEffect(() => {
    const lifetime = mount.current;
    lifetime.active = true;
    lifetime.generation++;
    return () => { lifetime.active = false; lifetime.generation++; };
  }, [owner]);
  const owns = (generation: number) => mount.current.active && mount.current.generation === generation && isCurrent();
  const album = useQuery({
    queryKey: [...albumKey(owner.userId ?? "-", petId), "reader", owner.mode, owner.serviceId, owner.epoch],
    queryFn: async ({ signal }) => {
      const generation = mount.current.generation;
      if (!owns(generation)) throw new AlbumScopeEnded();
      try {
        const result = await pets.album(petId, { limit: ALBUM_LIMIT }, signal);
        if (!owns(generation)) throw new AlbumScopeEnded();
        return result;
      } catch (error) {
        // Expired401 must be neutral before the shared QueryCache auth hook.
        if (!owns(generation)) throw new AlbumScopeEnded();
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  useEffect(() => { if (album.isError) setOpen(false); }, [album.isError]);
  // 桌上相框读不出来的那几张（照片 id＋地址）：换下一张；同一地址不再挂第二次
  const [frameFailed, setFrameFailed] = useState<ReadonlySet<string>>(() => new Set());
  // Household album permission is not social-public permission. The pure
  // historical helper stays unchanged; this consumer checks the live DTO.
  const photos = hangable(album.isError ? [] : album.data?.items ?? []).filter((photo) =>
    photo.pet_id === petId && typeof photo.asset.media_url === "string" && photo.asset.media_url.trim().length > 0 &&
    (photo.generation?.state === "ready" || (owner.mode === "fixture" && !photo.generation)) &&
    photo.category !== "fantasy" && photo.narrative !== "fictional_adventure"
  ).slice(0, ALBUM_LIMIT);
  if (!photos[0]) return null;
  const framed = photos.find((photo) => !frameFailed.has(mediaKey(photo))) ?? null;
  const where = framed ? placeText(framed) : null;
  const pct = (v: number, of: number) => `${((v / of) * 100).toFixed(2)}%`;
  return (
    <>
      {framed ? (
        <button
          type="button"
          className="ps-living-photo"
          aria-label={`${petName}带回来的照片${where ? `，在${where}拍的` : ""}`}
          data-testid="home-table-photo"
          data-thing="photo"
          style={{
            left: pct(PHOTO_FRAME.x - PHOTO_FRAME.width / 2, FRAME.width),
            top: pct(PHOTO_FRAME.y - PHOTO_FRAME.height - FRAME.top, FRAME.bottom - FRAME.top),
            width: pct(PHOTO_FRAME.width, FRAME.width),
            height: pct(PHOTO_FRAME.height, FRAME.bottom - FRAME.top),
          }}
          onClick={() => setOpen(true)}
        >
          <span className="ps-living-photo__frame">
            <img key={mediaKey(framed)} src={framed.asset.media_url!} alt="" onError={(event) => {
              // 只认这一刻挂着的这张：换了照片、换了主人以后，旧节点晚到的出错不算
              if (!isCurrent() || !event.currentTarget.isConnected) return;
              const failed = mediaKey(framed);
              setFrameFailed((old) => (old.has(failed) ? old : new Set(old).add(failed)));
            }} />
          </span>
        </button>
      ) : null}
      {open ? (
        <Sheet title={`${petName}带回来的照片`} subtitle={`最近 ${photos.length} 张`} onClose={() => setOpen(false)}>
          <ul className="ps-living-album" data-testid="home-album">
            {photos.map((photo) => {
              const meta = [placeText(photo), photo.album_day].filter(Boolean).join(" · ");
              return (
                <li key={photo.photo_id}>
                  <AlbumListImage key={JSON.stringify([owner.epoch, photo.photo_id, photo.asset.media_url])} photo={photo} petName={petName} isCurrent={isCurrent}
                    reading={album.isFetching} onRead={() => (isCurrent() ? album.refetch({ cancelRefetch: false }) : Promise.resolve())} />
                  {photo.caption ? <p>{photo.caption}</p> : null}
                  {meta ? <small>{meta}</small> : null}
                </li>
              );
            })}
          </ul>
        </Sheet>
      ) : null}
    </>
  );
}

/**
 * Only list media state. The existing parent owner/query owns authorization.
 * 读不出来（UI2-ALBUM-ERR-01）：那一格照旧一句话（照片信息仍在），下面一个「重新读取」重读这几张的照片信息——
 * 后端换了地址，这一格换新节点重新显示（key 带地址）；地址没变，照实说还没读到、过一会儿再来。读的时候按钮锁住。
 * 玩家亲手点一次，这一格也换一个新节点把同一张图再取一次（I 10-13 方案 2，R1）；不自动重试；又没读到时焦点回到新按钮。
 */
function AlbumListImage({ photo, petName, isCurrent, reading, onRead }: { photo: PetPhoto; petName: string; isCurrent: () => boolean; reading: boolean; onRead: () => Promise<unknown> }) {
  const [failed, setFailed] = useState(false);
  const [phase, setPhase] = useState<"idle" | "reading" | "read">("idle");
  const [attempt, setAttempt] = useState(0);
  const failureBox = useRef<HTMLDivElement>(null);
  useEffect(() => { if (failed && attempt > 0) failureBox.current?.querySelector("button")?.focus(); }, [failed, attempt]);
  const image = useRef<HTMLImageElement>(null);
  const active = useRef(false);
  useLayoutEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  // 按钮放在失败框里、那句话下面（框仍占原来 3:4 的位置，两列标题对齐）；播报只是那句话，按钮在播报节点外面。ps-stack 只借它的间距
  if (failed) return (
    <div ref={failureBox} className="ps-living-album__media-failure ps-stack">
      <span role="status" aria-live="polite">
        {phase === "read" && !reading ? "这张照片还没读到，过一会儿再来看看，照片信息仍在。" : "这张照片暂时无法显示，照片信息仍在。"}
      </span>
      <Button variant="ghost" loading={reading} onClick={() => {
        setPhase("reading");
        setAttempt((n) => n + 1);
        setFailed(false);
        void onRead().catch(() => undefined).finally(() => { if (active.current) setPhase("read"); });
      }}>重新读取</Button>
    </div>
  );
  return <img key={attempt} ref={image} src={photo.asset.media_url!} alt={photo.caption ?? `${petName}带回来的照片`} loading="lazy" onError={event => {
    if (active.current && isCurrent() && image.current === event.currentTarget && event.currentTarget.isConnected) setFailed(true);
  }} />;
}
